import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowsClockwise, Star } from "@phosphor-icons/react";
import api from "../lib/api";
import { StreakChip } from "../components/StreakChip";
import { useAuth } from "../context/AuthContext";

function StandingsTable({ division }) {
  const { user, toggleFavorite } = useAuth();
  const favs = (user && typeof user === "object" && user.favorites) || [];

  return (
    <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-white/10 bg-black/40 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{division.league_name}</div>
          <div className="font-display text-2xl tracking-wider">{division.division_name}</div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 border-b border-white/10">
              <th className="text-left py-3 px-3 font-semibold">Team</th>
              <th className="text-right py-3 px-2 font-semibold">W</th>
              <th className="text-right py-3 px-2 font-semibold">L</th>
              <th className="text-right py-3 px-2 font-semibold">PCT</th>
              <th className="text-right py-3 px-2 font-semibold">GB</th>
              <th className="text-right py-3 px-2 font-semibold">RUN DIFF</th>
              <th className="text-right py-3 px-2 font-semibold">L10</th>
              <th className="text-right py-3 px-3 font-semibold">STREAK</th>
            </tr>
          </thead>
          <tbody>
            {division.teams.map((t, idx) => {
              const isFav = favs.includes(t.id);
              return (
                <motion.tr
                  key={t.id}
                  initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.03 }}
                  className="border-b border-white/5 hover:bg-white/5 transition-colors group"
                  data-testid="team-standings-row"
                >
                  <td className="py-2.5 px-3">
                    <div className="flex items-center gap-2">
                      {user && typeof user === "object" && (
                        <button
                          onClick={() => toggleFavorite(t.id)}
                          data-testid={`favorite-toggle-${t.id}`}
                          className="text-zinc-600 hover:text-yellow-400"
                          aria-label="favorite"
                        >
                          <Star size={14} weight={isFav ? "fill" : "regular"} className={isFav ? "text-yellow-400" : ""} />
                        </button>
                      )}
                      <Link to={`/team/${t.id}`} className="font-head font-bold hover:text-[#007AFF]" data-testid={`team-link-${t.id}`}>
                        {t.name}
                      </Link>
                      <span className="text-[10px] font-mono-d text-zinc-600">{t.abbr}</span>
                    </div>
                  </td>
                  <td className="text-right px-2 font-mono-d tabular-nums">{t.wins}</td>
                  <td className="text-right px-2 font-mono-d tabular-nums text-zinc-400">{t.losses}</td>
                  <td className="text-right px-2 font-mono-d tabular-nums">{t.pct}</td>
                  <td className="text-right px-2 font-mono-d tabular-nums text-zinc-400">{t.gb}</td>
                  <td className={`text-right px-2 font-mono-d tabular-nums ${
                    (t.diff || 0) > 0 ? "text-[#22C55E]" : (t.diff || 0) < 0 ? "text-[#FF3B30]" : "text-zinc-400"
                  }`}>{t.diff > 0 ? `+${t.diff}` : t.diff}</td>
                  <td className="text-right px-2 font-mono-d tabular-nums text-zinc-300">
                    {t.last10 != null && t.last10_losses != null ? `${t.last10}-${t.last10_losses}` : "—"}
                  </td>
                  <td className="text-right px-3">
                    <StreakChip
                      type={t.streak.type} length={t.streak.number}
                      blip={t.streak.number === 1}
                      testId={`team-streak-${t.id}`}
                    />
                  </td>
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Standings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/mlb/standings");
      setData(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const byLeague = (id) => data?.divisions.filter((d) => d.league_id === id) || [];

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#007AFF] mb-1">Regular Season {data?.season || ""}</div>
          <h1 className="font-display text-5xl sm:text-6xl leading-none">STANDINGS</h1>
        </div>
        <button
          onClick={load} data-testid="standings-refresh-button"
          className="flex items-center gap-2 px-5 py-2.5 bg-transparent border border-white/20 hover:border-white/60 text-white rounded-sm text-xs uppercase tracking-wider font-bold transition-all hover:-translate-y-0.5"
        >
          <ArrowsClockwise size={16} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {!data ? (
        <div className="text-zinc-500 font-mono-d text-sm" data-testid="standings-loading">Loading standings…</div>
      ) : (
        <div className="space-y-12">
          <section>
            <h2 className="font-display text-3xl tracking-wider mb-4 text-zinc-300">AMERICAN LEAGUE</h2>
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              {byLeague(103).map((d) => <StandingsTable key={d.division_id} division={d} />)}
            </div>
          </section>
          <section>
            <h2 className="font-display text-3xl tracking-wider mb-4 text-zinc-300">NATIONAL LEAGUE</h2>
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              {byLeague(104).map((d) => <StandingsTable key={d.division_id} division={d} />)}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
